/**
 * O log do sistema — tudo que foi feito, e por quem.
 *
 * Os filtros vêm do DADO, não de uma lista fixa: a tela pergunta ao servidor
 * quais pessoas, módulos e tipos existem de verdade no log. Assim um módulo
 * novo aparece no filtro sozinho, e um que nunca foi usado não polui a tela
 * com uma opção que devolve zero.
 *
 * O PADRÃO É 30 DIAS, e não "tudo". A tabela só cresce; abrir a tela puxando
 * o histórico inteiro ficaria mais lento a cada semana até alguém desistir de
 * usar — e um log que ninguém abre não serve para nada.
 */

import { api, ErroApi } from '../../api.js';
import { escapar, dataHora } from '../../comum/formato.js';

interface Registro {
  id: number;
  ocorrido_em: string;
  pessoa_id: number | null;
  pessoa_nome: string | null;
  modulo: string;
  tipo: string;
  alvo: string | null;
  alvo_id: string | null;
  resumo: string;
  metodo: string | null;
  caminho: string | null;
  detalhes: Record<string, unknown> | null;
}

interface Filtros {
  pessoas: { pessoa_id: number; pessoa_nome: string; n: number }[];
  modulos: { modulo: string; n: number }[];
  tipos: { tipo: string; n: number }[];
}

const TIPO: Record<string, { rotulo: string; classe: string }> = {
  acesso: { rotulo: 'Acesso', classe: 't-acesso' },
  cadastro: { rotulo: 'Cadastro', classe: 't-cadastro' },
  alteracao: { rotulo: 'Alteração', classe: 't-alteracao' },
  exclusao: { rotulo: 'Exclusão', classe: 't-exclusao' },
  movimento: { rotulo: 'Movimento de estoque', classe: 't-movimento' },
  aprovacao: { rotulo: 'Aprovação', classe: 't-aprovacao' },
  finalizacao: { rotulo: 'Finalização', classe: 't-finalizacao' },
  outro: { rotulo: 'Outro', classe: 't-outro' },
};

const MODULO: Record<string, string> = {
  desk: 'Escritório',
  entrada: 'Entrada de material',
  motorista: 'Motorista',
  pedidos: 'Pedidos',
  master: 'Administração',
  sistema: 'Sistema',
};

export function montarLog(raiz: HTMLElement): void {
  const estado = { pessoa: '', modulo: '', tipo: '', busca: '', dias: '30', pagina: 1 };
  let ultima = 0;

  raiz.innerHTML = `
    <div class="barra-busca">
      <input id="log-busca" placeholder="Procure no que foi feito ou em quem fez…" autocomplete="off">
      <select id="log-pessoa"><option value="">Todas as pessoas</option></select>
      <select id="log-modulo"><option value="">Todos os módulos</option></select>
      <select id="log-tipo"><option value="">Todos os tipos</option></select>
      <select id="log-dias">
        <option value="1">Hoje</option>
        <option value="7">7 dias</option>
        <option value="30" selected>30 dias</option>
        <option value="90">90 dias</option>
        <option value="365">1 ano</option>
      </select>
    </div>
    <div id="log-lista"><p class="carregando">Carregando…</p></div>
    <div class="paginacao" id="log-pag"></div>`;

  const lista = raiz.querySelector<HTMLElement>('#log-lista')!;
  const pag = raiz.querySelector<HTMLElement>('#log-pag')!;

  // Cada filtro reinicia a paginação: continuar na página 7 de um recorte que
  // agora tem 2 páginas mostraria vazio e pareceria erro.
  for (const [id, campo] of [
    ['#log-pessoa', 'pessoa'],
    ['#log-modulo', 'modulo'],
    ['#log-tipo', 'tipo'],
    ['#log-dias', 'dias'],
  ] as const) {
    raiz.querySelector<HTMLSelectElement>(id)!.addEventListener('change', (e) => {
      estado[campo] = (e.target as HTMLSelectElement).value;
      estado.pagina = 1;
      void carregar();
    });
  }

  let temporizador: number | undefined;
  raiz.querySelector<HTMLInputElement>('#log-busca')!.addEventListener('input', (e) => {
    window.clearTimeout(temporizador);
    const v = (e.target as HTMLInputElement).value.trim();
    temporizador = window.setTimeout(() => {
      estado.busca = v;
      estado.pagina = 1;
      void carregar();
    }, 300);
  });

  async function carregarFiltros() {
    try {
      const f = await api.get<Filtros>('/api/master/log/filtros');
      const encher = (id: string, opcoes: { valor: string; rotulo: string; n: number }[]) => {
        const sel = raiz.querySelector<HTMLSelectElement>(id)!;
        const primeiro = sel.options[0]!.outerHTML;
        sel.innerHTML =
          primeiro +
          opcoes
            .map((o) => `<option value="${escapar(o.valor)}">${escapar(o.rotulo)} (${o.n})</option>`)
            .join('');
      };
      encher('#log-pessoa', f.pessoas.map((p) => ({ valor: String(p.pessoa_id), rotulo: p.pessoa_nome, n: p.n })));
      encher('#log-modulo', f.modulos.map((m) => ({ valor: m.modulo, rotulo: MODULO[m.modulo] ?? m.modulo, n: m.n })));
      encher('#log-tipo', f.tipos.map((t) => ({ valor: t.tipo, rotulo: TIPO[t.tipo]?.rotulo ?? t.tipo, n: t.n })));
    } catch {
      // Sem filtros a lista ainda funciona; só não dá para recortar.
    }
  }

  async function carregar() {
    const meu = ++ultima;
    const p = new URLSearchParams({ dias: estado.dias, pagina: String(estado.pagina), porPagina: '100' });
    if (estado.pessoa) p.set('pessoa', estado.pessoa);
    if (estado.modulo) p.set('modulo', estado.modulo);
    if (estado.tipo) p.set('tipo', estado.tipo);
    if (estado.busca) p.set('busca', estado.busca);

    try {
      const r = await api.get<{ total: number; pagina: number; paginas: number; registros: Registro[] }>(
        `/api/master/log?${p}`,
      );
      if (meu !== ultima) return;
      desenhar(r);
    } catch (erro) {
      if (meu !== ultima) return;
      lista.innerHTML = `<p class="aviso">${escapar(
        erro instanceof ErroApi ? erro.message : 'Não consegui carregar o log.',
      )}</p>`;
    }
  }

  function desenhar(r: { total: number; pagina: number; paginas: number; registros: Registro[] }) {
    if (r.registros.length === 0) {
      lista.innerHTML = `<div class="vazio"><p>Nada registrado neste recorte.</p></div>`;
      pag.innerHTML = '';
      return;
    }

    lista.innerHTML = `
      <p class="dica">${r.total} registro(s).</p>
      <div class="rolagem">
        <table class="tabela">
          <thead><tr><th>Quando</th><th>Quem</th><th>Módulo</th><th>Tipo</th><th>O que fez</th></tr></thead>
          <tbody>${r.registros.map(linha).join('')}</tbody>
        </table>
      </div>`;

    // Os detalhes ficam escondidos até alguém pedir: a lista é para varrer, e
    // um JSON aberto em cada linha tornaria isso impossível.
    lista.querySelectorAll<HTMLElement>('[data-detalhe]').forEach((b) => {
      b.addEventListener('click', () => {
        const alvo = lista.querySelector<HTMLElement>(`#det-${b.dataset['detalhe']}`);
        if (alvo) alvo.hidden = !alvo.hidden;
      });
    });

    pag.innerHTML =
      r.paginas <= 1
        ? ''
        : `<button class="btn btn-claro" id="ant" ${r.pagina <= 1 ? 'disabled' : ''}>← Anterior</button>
           <span>Página ${r.pagina} de ${r.paginas}</span>
           <button class="btn btn-claro" id="prox" ${r.pagina >= r.paginas ? 'disabled' : ''}>Próxima →</button>`;
    pag.querySelector('#ant')?.addEventListener('click', () => {
      estado.pagina--;
      void carregar();
    });
    pag.querySelector('#prox')?.addEventListener('click', () => {
      estado.pagina++;
      void carregar();
    });
  }

  function linha(l: Registro): string {
    const t = TIPO[l.tipo] ?? { rotulo: l.tipo, classe: 't-outro' };
    const temDetalhe = l.detalhes && Object.keys(l.detalhes).length > 0;
    return `
      <tr>
        <td class="data">${dataHora(l.ocorrido_em)}</td>
        <td>${escapar(l.pessoa_nome ?? '—')}</td>
        <td>${escapar(MODULO[l.modulo] ?? l.modulo)}</td>
        <td><span class="log-tipo ${t.classe}">${escapar(t.rotulo)}</span></td>
        <td>
          ${escapar(l.resumo)}
          ${
            temDetalhe
              ? `<button class="log-mais" data-detalhe="${l.id}">detalhes</button>
                 <pre class="log-detalhe" id="det-${l.id}" hidden>${escapar(
                   JSON.stringify(l.detalhes, null, 1),
                 )}\n${escapar(`${l.metodo ?? ''} ${l.caminho ?? ''}`)}</pre>`
              : ''
          }
        </td>
      </tr>`;
  }

  void carregarFiltros();
  void carregar();
}
